<!-- OwnedSubscriptions.vue -->
<template>
    <div class="payment-container">
        <div class="subscription-content owned" :class="{'reach-limit': !isSubscriptionAvailable(item)}" v-for="item in ownedSubscriptionList" :key="item.id">
            <div class="subscribed-badge">
                <SubscribedBadgeIcon :isActive="isSubscriptionAvailable(item)" />
                <span class="label">{{ item.name }}</span>
            </div>
            <div class="subscribed-type" v-for="title in [getSubscriptionTitle(item)]" :key="title.type">
                <span class="type">{{ title.type }}</span>
                <span class="usage" v-if="title.usage">{{ title.usage }}</span>
            </div>
            <template v-if="isSubscriptionAvailable(item)">
                <pre v-if="item.detail" class="detail">{{ item.detail }}</pre>
                <div v-else class="list-style">
                    <span class="li" v-for="(line, index) in formatSubscriptionInfo(
                        item,
                        currencyFormatBillingRule(billingRule.price, billingRule, billingRule.currency),
                        billingRule.price_time,
                        billingRule.top_limit ? currencyFormatBillingRule(billingRule.top_limit, billingRule, billingRule.currency) : undefined)"
                        :key="index">
                        {{ line }}
                    </span>
                </div>
                <span v-if="billingRuleTaxText" class="tax-hint">{{ billingRuleTaxText }}</span>
            </template>
            <template v-else>
                <span class="reach-limit-explain">{{ $t('m.reachLimit') }}</span>
            </template>
            <div
                v-if="authorizationRequired && isSubscriptionAvailable(item)"
                class="authorization-reminder"
            >
                <span class="text highlight">{{ $t('m.paymentAuthorizationRequired') }}</span>
                <span class="help-icon" v-active-touch @click="isShowAuthorizationPopup = true">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" />
                        <path
                            d="M9.6 9.2a2.6 2.6 0 1 1 4.4 1.9c-.8.6-1.3 1-1.3 2v.2"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                        />
                        <circle cx="12" cy="17.2" r="1" fill="currentColor" />
                    </svg>
                </span>
            </div>
        </div>
        <PopupConfirm
            show-header
            :title="$t('m.paymentAuthorizationTitle')"
            :showPopup="isShowAuthorizationPopup"
            @hidePopup="isShowAuthorizationPopup = false"
        >
            <div class="authorization-popup">
                <div class="content">
                    <div v-if="authorizationDesc1" class="line">{{ authorizationDesc1 }}</div>
                    <div class="line">{{ authorizationDesc2 }}</div>
                    <div class="line">{{ authorizationDesc3 }}</div>
                    <div class="line emphasis">{{ authorizationDesc4 }}</div>
                </div>
            </div>
        </PopupConfirm>
    </div>
</template>

<script>
import SubscribedBadgeIcon from '../../../components/icons/SubscribedBadgeIcon.vue';
import PopupConfirm from "../../../components/popups/PopupConfirm.vue";

export default {
    components: {
        SubscribedBadgeIcon,
        PopupConfirm
    },
    props: {
        billingRule: {
            type: Object,
            required: true
        },
        ownedSubscriptionList: {
            type: Array,
            required: true
        },
        authorizationRequired: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isShowAuthorizationPopup: false
        };
    },
    computed: {
        billingRuleTaxText() {
            return this.getBillingRuleTaxText(this.billingRule)
        },
        depositDisplay() {
            const deposit = Number(this.billingRule?.deposit ?? 0);
            const currency = this.billingRule?.currency;
            return this.formatBillingRuleAmountLabel(deposit, this.billingRule, currency);
        },
        authorizationDesc1() {
            const text = this.$t('m.paymentAuthorizationDesc1');
            return text === 'm.paymentAuthorizationDesc1' ? '' : text;
        },
        authorizationDesc2() {
            return this.$t('m.paymentAuthorizationDesc2');
        },
        authorizationDesc3() {
            return this.$t('m.paymentAuthorizationDesc3').replace(
                '[amount]',
                this.depositDisplay
            );
        },
        authorizationDesc4() {
            return this.$t('m.paymentAuthorizationDesc4');
        }
    },
    methods: {
        isSubscriptionAvailable(item) {
            if (item.available_subscription !== undefined) {
                return item.available_subscription === 1;
            }
            return item.subscription_status === 1;
        },
        getSubscriptionTitle(item) {
            return this.formatSubscriptionTitle(
                item.discount_type,
                item.discount_value,
                item.usage
            )
        },
    }
}
</script>
<style lang="scss">
@use '@/styles/variables.scss' as *;

.payment-container {
  margin-top: 20px;
  display: flex;
  gap: 9px;
  flex-direction: column;
  align-items: center;
}

.subscription-content.owned {
  width: $container-width;
  background: var(--theme-selectable-accent-bg);
  padding: 20px 15px;
  border-radius: $border-radius;
  font-family: $font-family;
  font-weight: 400;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  color: $secondary-color;
  box-shadow: $shadow;
  position: relative;
  border: 1px solid var(--theme-selectable-accent-color);

  &.reach-limit {
    background: $background-white;
    border-color: transparent;
    color: #E5E5E5;

    .subscribed-badge {
      color: #CFCFCF;

      .label {
        background: #A1A1A114;
      }
    }

    .subscribed-type .type,
    .subscribed-type .usage {
      color: #E5E5E5;
    }
  }

  .tax-hint {
    font-size: 12px;
    line-height: 17px;
    color: #8a8a8a;
  }
}

.subscribed-badge {
  color: var(--theme-selectable-accent-color);
  font-family: $font-family;
  font-weight: 500;
  font-size: 12px;
  height: 20px;
  position: relative;

  .icon {
    width: 21px;
    height: 21px;
    position: absolute;
    left: 0;
    top: 0;
  }

  .label {
    display: inline-block;
    line-height: 20px;
    height: 20px;
    padding: 0 10px 0 15px;
    margin-left: 12px;
    background: var(--theme-selectable-accent-bg);
    border-top-right-radius: 2px;
    border-bottom-right-radius: 2px;
  }
}

.subscribed-type {
  font-family: $font-family-display;
  line-height: 22px;

  .type {
    font-weight: 600;
    font-size: 20px;
    color: var(--theme-selectable-accent-color);
  }

  .usage {
    font-weight: 500;
    font-size: 14px;
    color: #999;
  }
}

.reach-limit-explain {
  font-family: $font-family-display;
  font-weight: 500;
  font-size: 14px;
}

.list-style {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .li {
    width: 100%;
    position: relative;
    display: inline-block;
    padding-left: 14px;
    font-size: 12px;
    font-weight: 400;
    line-height: 17.28px;

    &::before {
      content: '';
      position: absolute;
      left: 1px;
      top: 6px;
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background-color: $secondary-color;
    }
  }
}

.list-style + .tax-hint {
  width: 100%;
  position: relative;
  display: inline-block;
  padding-left: 14px;

  &::before {
    content: '';
    position: absolute;
    left: 1px;
    top: 6px;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background-color: $secondary-color;
  }
}

.authorization-reminder {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-family: $font-family;
  font-weight: 500;
  font-size: 12px;
  color: $secondary-color;

  .text {
    flex: 1;
  }

  .text.highlight {
    color: var(--theme-selectable-accent-color);
    font-weight: 600;
  }

  .help-icon {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--theme-selectable-accent-color);
  }

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
}

.authorization-popup {
  position: relative;

  .content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: $font-family;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: $secondary-color;
  }

  .line.emphasis {
    color: $text-color;
    font-weight: 600;
  }
}
</style>
